import { createCameraVideoTrack, createMicrophoneAudioTrack } from "@videosdk.live/react-sdk";

const useMediaStream = () => {

  // Device capability detection
  const isMobile = () => window.matchMedia("(max-width: 768px)").matches;
  const isLowEndDevice = () => {
    return navigator.hardwareConcurrency <= 4 || 
           (navigator.deviceMemory && navigator.deviceMemory <= 4);
  };
  const getNetworkQuality = () => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return 'unknown';
    
    if (connection.effectiveType === '4g' && connection.downlink >= 10) return 'high';
    if (connection.effectiveType === '4g' || connection.downlink >= 5) return 'medium';
    return 'low';
  };

  // Optimized video track configuration
  const getOptimizedVideoConfig = (options = {}) => {
    const { participantCount = 2, contentType = 'motion', forcedConfig = null } = options;
    
    if (forcedConfig) return forcedConfig;
    
    const mobile = isMobile();
    const lowEnd = isLowEndDevice();
    const networkQuality = getNetworkQuality();
    
    // Configuration matrix based on device and network conditions
    let config = {
      encoderConfig: "h720p_w1280p",
      optimizationMode: contentType, // 'motion', 'text', or 'detail'
      multiStream: true,
      facingMode: mobile ? "user" : "environment"
    };
    
    // Adjust for mobile devices
    if (mobile) {
      if (participantCount <= 4) {
        config.encoderConfig = lowEnd ? "h360p_w640p" : "h480p_w640p";
      } else {
        config.encoderConfig = "h360p_w640p";
      }
    } else {
      // Desktop optimization
      if (participantCount <= 4) {
        config.encoderConfig = lowEnd ? "h540p_w960p" : "h720p_w1280p";
      } else if (participantCount <= 8) {
        config.encoderConfig = "h480p_w640p";
      } else {
        config.encoderConfig = "h360p_w640p";
      }
    }
    
    // Network-based adjustments
    if (networkQuality === 'low') {
      config.encoderConfig = mobile ? "h240p_w320p" : "h360p_w640p";
      config.multiStream = false; // Save bandwidth
    } else if (networkQuality === 'medium') {
      config.multiStream = participantCount <= 6;
    }
    
    // Content-type specific optimizations
    if (contentType === 'text') {
      // For presentations/text sharing - prioritize resolution over framerate
      config.multiStream = true; // Better for screen sharing scenarios
    } else if (contentType === 'detail') {
      // For detailed content like artwork
      if (!mobile && networkQuality !== 'low') {
        config.encoderConfig = "h720p_w1280p";
      }
    }
    
    return config;
  };

  const getVideoTrack = async ({ 
    webcamId, 
    encoderConfig, 
    participantCount, 
    contentType = 'motion',
    customConfig = null 
  }) => {
    try {
      const config = customConfig || getOptimizedVideoConfig({
        participantCount,
        contentType,
        forcedConfig: encoderConfig ? { encoderConfig } : null
      });

      const track = await createCameraVideoTrack({
        cameraId: webcamId,
        encoderConfig: config.encoderConfig,
        optimizationMode: config.optimizationMode,
        multiStream: config.multiStream,
        facingMode: config.facingMode,
      });

      return track;
    } catch(error) {
      console.error('Error creating video track:', error);
      return null;
    }
  };

  // Audio optimization configuration based on use case and environment
  const getOptimizedAudioConfig = (options = {}) => {
    const { 
      useCase = 'meeting', // 'meeting', 'music', 'highQuality', 'lowBandwidth'
      participantCount = 2,
      forcedConfig = null
    } = options;
    
    if (forcedConfig) return forcedConfig;
    
    const mobile = isMobile();
    const lowEnd = isLowEndDevice();
    const networkQuality = getNetworkQuality();
    
    let config = {
      encoderConfig: "speech_standard",
      noiseConfig: {
        noiseSuppression: true,
        echoCancellation: true,
        autoGainControl: true
      }
    };
    
    // Use case specific optimizations
    switch (useCase) {
      case 'music':
      case 'musicSharing':
        config.encoderConfig = "music_standard";
        config.noiseConfig = {
          noiseSuppression: false, // Preserve musical elements
          echoCancellation: true,
          autoGainControl: false // Preserve dynamic range
        };
        break;
        
      case 'highQuality':
      case 'broadcast':
        config.encoderConfig = "high_quality";
        config.noiseConfig = {
          noiseSuppression: true,
          echoCancellation: true,
          autoGainControl: true
        };
        break;
        
      case 'lowBandwidth':
      case 'poor_connection':
        config.encoderConfig = "speech_standard";
        config.noiseConfig = {
          noiseSuppression: true, // More important on poor connections
          echoCancellation: true,
          autoGainControl: true
        };
        break;
        
      default: // meeting, conference
        config.encoderConfig = "speech_standard";
        config.noiseConfig = {
          noiseSuppression: true,
          echoCancellation: true,
          autoGainControl: true
        };
    }
    
    // Device and network based adjustments
    if (mobile || lowEnd) {
      // More aggressive noise suppression on mobile
      config.noiseConfig.noiseSuppression = true;
      config.noiseConfig.autoGainControl = true;
    }
    
    if (networkQuality === 'low') {
      // Prioritize speech clarity on poor connections
      config.encoderConfig = "speech_standard";
      config.noiseConfig.noiseSuppression = true;
    }
    
    // Large meetings optimization
    if (participantCount > 8) {
      config.noiseConfig.noiseSuppression = true; // More important in large meetings
      config.noiseConfig.autoGainControl = true;
    }
    
    return config;
  };

  const getAudioTrack = async ({
    micId, 
    useCase = 'meeting',
    participantCount,
    customConfig = null
  }) => {
    try{
      const config = customConfig || getOptimizedAudioConfig({
        useCase,
        participantCount,
        forcedConfig: null
      });
      
      const track = await createMicrophoneAudioTrack({
        microphoneId: micId,
        encoderConfig: config.encoderConfig,
        noiseConfig: config.noiseConfig
      });
      return track;
    } catch(error) {
      console.error('Error creating audio track:', error);
      return null;
    }
  };

  // Screen share optimization
  const getOptimizedScreenShareConfig = (contentType = 'motion') => {
    const mobile = isMobile();
    const networkQuality = getNetworkQuality();
    
    let config = {
      optimizationMode: contentType,
      encoderConfig: "h720p_15fps"
    };
    
    // Adjust for content type
    if (contentType === 'text' || contentType === 'detail') {
      config.encoderConfig = mobile ? "h720p_5fps" : "h1080p_15fps";
      config.optimizationMode = 'text'; // Prioritize resolution over framerate
    } else if (contentType === 'motion') {
      config.encoderConfig = networkQuality === 'low' ? "h480p_15fps" : "h720p_15fps";
    }
    
    return config;
  };

  return { 
    getVideoTrack, 
    getAudioTrack, 
    getOptimizedVideoConfig,
    getOptimizedAudioConfig,
    getOptimizedScreenShareConfig,
    // Utility functions for external use
    isMobile: isMobile(),
    isLowEndDevice: isLowEndDevice(),
    networkQuality: getNetworkQuality()
  };
};

export default useMediaStream;
